import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';

import './styles/picker.css';

// A picker that searches the server instead of listing the first hundred rows.
//
// WHY IT EXISTS. Every <select> in this panel asked for `limit: 100` and hoped.
// That is honest while a table is small and a trap the day it is not: whoever
// is not in the first hundred cannot be chosen at all, and there is no way to
// look for them. Since tier 12 the four entities used as pickers — clients,
// users, teams, projects — take `?q=`, so the list can come from the search
// instead of from luck.
//
// It is deliberately NOT a full ARIA combobox. It is a button that opens a
// panel with a search field and a list of buttons: everything in it is
// focusable and operable with the keyboard by being what it is, without a
// roving tabindex to maintain. Escape closes it.

const OPTIONS_LIMIT = 20;

export default function Picker({
    value,
    onChange,
    // (q, signal) => Promise<item[]> — the feature owns its endpoint.
    fetchOptions,
    cacheKey,
    labelOf,
    subtitleOf,
    // The label of the current value when this screen already knows it, which
    // is the edit case: the record arrived with its name inside.
    selectedLabel,
    // (id, signal) => Promise<item|null>. Resolves the CURRENT value when the
    // screen does not already know its name — see the block below.
    resolveOption,
    // Said out loud when the list is not searchable by text, so "type to search
    // among all of them" does not appear over a box that ignores what is typed.
    searchable = true,
    // Overrides the footer line. It exists because «buscá entre todos» is not
    // always true: a picker that filters an already-loaded catalog searches
    // among the hundred rows it has, and saying otherwise promises a reach it
    // does not have.
    hint,
    placeholder = 'Elegir...',
    emptyLabel,
    disabled,
    invalid,
}) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');
    // Remembers what was picked here, so the button can show a name without
    // asking the server again for something it just had in its hands.
    const [chosen, setChosen] = useState(null);

    const boxRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [text]);

    // Closing on an outside click and on Escape. Both, because a panel that
    // only closes one way is a panel somebody leaves open.
    useEffect(() => {
        if (!open) return undefined;

        const onDown = (event) => {
            if (!boxRef.current?.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const { data: options = [], isFetching } = useQuery({
        queryKey: [cacheKey, 'picker', debounced],
        queryFn: ({ signal }) => fetchOptions(debounced, signal),
        // Only while the panel is open: a form with four pickers must not fire
        // four searches nobody asked for.
        enabled: open,
        staleTime: 30_000,
    });

    // RESOLVING THE VALUE THAT IS ALREADY SET, and why it is its own request.
    //
    // A saved row points at an id. If that id is not in the page the picker
    // happens to be showing, the button used to fall back to `#57` — which is
    // not wrong, but reads as missing data, and is the failure nobody notices:
    // it looks the same whether the catalog is short or the record is simply
    // past the first hundred rows.
    //
    // So when there is a value, nobody handed us a label, and nothing was
    // picked here, the current record is fetched BY ID. One request, cached
    // under the feature's own detail key, and only when it is actually needed.
    const needsResolving = !!value && !chosen && !selectedLabel && !!resolveOption;

    const { data: resolved } = useQuery({
        queryKey: [cacheKey, 'resolve', String(value)],
        queryFn: ({ signal }) => resolveOption(value, signal),
        enabled: needsResolving,
        staleTime: 5 * 60_000,
    });

    const pick = (option) => {
        setChosen(option);
        onChange(String(option.id));
        setOpen(false);
        setText('');
    };

    const clear = () => {
        setChosen(null);
        onChange('');
        setOpen(false);
        setText('');
    };

    // In order: what was picked here, what the screen already knew, what the
    // resolver found. `#id` is the last resort and never a blank cell — less,
    // but never wrong.
    const shown = chosen ? labelOf(chosen)
        : selectedLabel
        || (resolved ? labelOf(resolved) : '')
        || (value ? `#${value}` : '');

    return (
        <div className="picker" ref={boxRef}>
            <button
                type="button"
                className={`picker__button ${invalid ? 'picker__button--invalid' : ''}`}
                onClick={() => setOpen(v => !v)}
                disabled={disabled}
                aria-expanded={open}
            >
                <span className={shown ? 'picker__value' : 'picker__placeholder'}>
                    {shown || placeholder}
                </span>
                <span className="picker__caret" aria-hidden="true">▾</span>
            </button>

            {open && (
                <div className="picker__panel">
                    {/* No search box when the endpoint has no `q`. Since tier 12
                        only clients, users, teams and projects take one; tiers,
                        repositories and tasks do not. Drawing a field that
                        ignores what is typed is worse than not drawing it. */}
                    {searchable && (
                        <input
                            className="picker__search"
                            type="search"
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder="Buscar..."
                            aria-label="Buscar"
                            /* Focused on open, which is the one case where taking
                               focus is right: the panel exists to be typed into. */
                            autoFocus
                        />
                    )}

                    <ul className="picker__list">
                        {emptyLabel && (
                            <li>
                                <button type="button" className="picker__option" onClick={clear}>
                                    {emptyLabel}
                                </button>
                            </li>
                        )}

                        {options.map(option => (
                            <li key={option.id}>
                                <button
                                    type="button"
                                    className={`picker__option ${String(option.id) === String(value) ? 'picker__option--current' : ''}`}
                                    onClick={() => pick(option)}
                                >
                                    <span className="picker__option-label">{labelOf(option)}</span>
                                    {subtitleOf && (
                                        <span className="picker__option-sub">{subtitleOf(option)}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Under two characters the backend ignores `q` and answers the
                        plain list, so "no matches" only ever means what it says. */}
                    {!isFetching && !options.length && (
                        <p className="picker__none">
                            {debounced ? `Nada coincide con "${debounced}".` : 'No hay nada para elegir.'}
                        </p>
                    )}

                    {isFetching && <p className="picker__none">Buscando...</p>}

                    <p className="picker__hint">
                        {hint ?? (searchable
                            ? `Se muestran hasta ${OPTIONS_LIMIT}. Escribí para buscar entre todos.`
                            : `Se muestran hasta ${OPTIONS_LIMIT}. Esta lista no se busca por texto: se acota por proyecto.`)}
                    </p>
                </div>
            )}
        </div>
    );
}

export { OPTIONS_LIMIT };
